import { convertedQtyFormatterConfig } from '@/config/config';
import { mergeRefs } from '@/utils/common';
import useLanguage from '@/utils/hooks/useLanguage';
import BigNumber from 'bignumber.js';
import cn from 'classnames';
import { ComponentProps, forwardRef, useEffect, useRef } from 'react';
import { useLocale, useNumberField } from 'react-aria';
import { useFormContext } from 'react-hook-form';
import { useNumberFieldState } from 'react-stately';

interface Props extends ComponentProps<'input'> {
    step?: number;
    label?: string;
    className?: string;
    defaultValue?: string | number;
    defaultSlippage?: string;
}

const SlippageInput = forwardRef<HTMLInputElement, Props>((props, ref: any) => {
    const { name = '', step = 1, label, disabled, defaultValue, defaultSlippage, className } = props;
    const { getValues, setValue, watch } = useFormContext();

    const inputRef = useRef(null);
    const { locale } = useLocale();
    const lang = useLanguage();

    const state = useNumberFieldState({ locale, formatOptions: convertedQtyFormatterConfig });
    const { labelProps, groupProps, inputProps } = useNumberField({ label: label || name }, state, inputRef);

    const value = watch(name);

    useEffect(() => {
        if (value !== defaultSlippage && value !== state.numberValue && !isNaN(state.numberValue)) {
            setValue(name, state.numberValue, { shouldValidate: true, shouldDirty: true });
        } else if (isNaN(state.numberValue)) {
            setValue(name, 0);
        }
    }, [state.numberValue]);

    useEffect(() => {
        if (value !== defaultSlippage && value !== state.numberValue) {
            state.setNumberValue(value);
        }
    }, [value]);

    return (
        <div className="w-full gap-1 flex flex-col">
            <label {...labelProps} className="block text-sm font-medium text-brand-text">
                {label}
            </label>
            <div {...groupProps} className="relative flex">
                <input
                    {...inputProps}
                    ref={mergeRefs(inputRef, ref)}
                    value={state.numberValue === 0 ? defaultSlippage : state.inputValue}
                    placeholder={props.placeholder}
                    autoComplete="off"
                    className={cn(
                        'appearance-none block w-full px-3 py-2 bg-brand-background-dark border border-brand-neutral-700 rounded-md text-sm placeholder-brand-neutral-500 focus:outline-none focus:ring-brand-green focus:border-brand-green',
                        { 'text-brand-neutral-400 cursor-not-allowed': disabled },
                        { 'text-brand-text': !className },
                        className
                    )}
                    disabled={disabled}
                />
                <div
                    className={cn('absolute flex flex-col h-full justify-center text-xs text-brand-text w-5', {
                        'right-2': lang.dir === 'ltr',
                        'left-2': lang.dir === 'rtl'
                    })}>
                    <button
                        type="button"
                        className={cn('px-0', {
                            'text-brand-neutral-400 cursor-not-allowed': disabled,
                            'hover:text-brand-green-light': !disabled
                        })}
                        onClick={() => {
                            if (!disabled) {
                                const prevValue = isNaN(getValues(name)) ? 0 : getValues(name);
                                const nextValue = BigNumber(prevValue).plus(step).toNumber();
                                setValue(name, nextValue, { shouldValidate: true });
                            }
                        }}>
                        &#x25B2;
                    </button>
                    <button
                        type="button"
                        className={cn('px-0', {
                            'text-brand-neutral-400 cursor-not-allowed': disabled,
                            'hover:text-brand-green-light': !disabled
                        })}
                        onClick={() => {
                            if (!disabled) {
                                const prevValue = isNaN(getValues(name)) ? 0 : getValues(name);
                                const nextValue = BigNumber(prevValue).minus(step).toNumber();
                                if (nextValue <= 0) setValue(name, 0, { shouldValidate: true });
                                else setValue(name, nextValue, { shouldValidate: true });
                            }
                        }}>
                        &#x25BC;
                    </button>
                </div>
            </div>
        </div>
    );
});

SlippageInput.displayName = 'SlippageInput';

export default SlippageInput;
